import { DateTime } from "luxon";
import { z } from "zod";

import type { GoogleSheetsRange } from "@/src/lib/google-sheets";

export const insuranceTierSnapshotSchema = z.object({
  sourceId: z.string().min(1),
  version: z.string().min(1),
  capturedAt: z.iso.datetime({ offset: true }),
  rows: z.array(z.array(z.union([z.string(), z.number().finite(), z.boolean()]))).min(2),
});
export type InsuranceTierSnapshot = z.infer<typeof insuranceTierSnapshotSchema>;

export interface InsuranceTierSnapshotConfig {
  range: GoogleSheetsRange;
  headers: readonly string[];
  timezone: string;
  refreshHour: number;
  graceMinutes: number;
}

type RefreshSchedule = Pick<InsuranceTierSnapshotConfig, "timezone" | "refreshHour" | "graceMinutes">;

export function insuranceTierRefreshBoundary(now: string, schedule: RefreshSchedule): DateTime {
  const current = DateTime.fromISO(now, { zone: schedule.timezone });
  const today = current.startOf("day").set({ hour: schedule.refreshHour });

  return current.toMillis() < today.toMillis() ? today.minus({ days: 1 }) : today;
}

export function isInsuranceTierSnapshotCurrent(capturedAt: string, now: string, schedule: RefreshSchedule): boolean {
  const current = DateTime.fromISO(now, { zone: schedule.timezone });
  const captured = DateTime.fromISO(capturedAt, { zone: schedule.timezone });

  if (!current.isValid || !captured.isValid) return false;

  const currentMillis = current.toMillis();
  const capturedMillis = captured.toMillis();
  const boundary = insuranceTierRefreshBoundary(now, schedule);
  // During refresh/retries, yesterday's successful snapshot remains usable for a short grace period.
  const earliest =
    currentMillis < boundary.plus({ minutes: schedule.graceMinutes }).toMillis()
      ? boundary.minus({ days: 1 })
      : boundary;

  return capturedMillis >= earliest.toMillis() && capturedMillis <= currentMillis;
}

const normalize = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/** Validate the entire source before publishing any version. Blank tiers remain unresolved. */
export function validateInsuranceTierRows(
  rows: InsuranceTierSnapshot["rows"],
  config: InsuranceTierSnapshotConfig
): void {
  const { range, headers } = config;

  if (rows.length < 2 || rows.length >= range.endRow - range.startRow + 1) {
    throw new Error("Insurance tier sheet is empty or may be truncated; expand its configured range");
  }

  if (
    headers.length !== range.endColumn - range.startColumn + 1 ||
    rows[0].length !== headers.length ||
    !headers.every((header, index) => normalize(rows[0][index]) === normalize(header))
  ) {
    throw new Error("Insurance tier sheet headers do not match the configured layout");
  }

  const payers = new Set<string>();

  for (const [index, row] of rows.slice(1).entries()) {
    if (row.every((cell) => normalize(cell) === "")) continue;
    const payer = normalize(row[0]);

    if (!payer || payers.has(payer) || row.length > headers.length) {
      throw new Error(
        `Insurance tier sheet has a missing/duplicate payer or extra columns at row ${range.startRow + index + 1}`
      );
    }

    payers.add(payer);
    if (row.slice(1).some((cell) => !["", "tier 1", "tier 2", "not accepted"].includes(normalize(cell)))) {
      throw new Error(`Insurance tier sheet has an unsupported tier at row ${range.startRow + index + 1}`);
    }
  }

  if (!payers.size) throw new Error("Insurance tier sheet contains no payers");
}
